import { combineLatest, Subscription } from 'rxjs';
import { Component, OnInit, ChangeDetectionStrategy, signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { GameQuery } from 'src/app/state/gameState/game.query';
import {
  ICurrentCountryPosition,
  IDimension,
  IHelpTopic,
  ITopic,
} from '../../models/api.model';
import { IUserGame } from 'src/app/models/api.model';
import { GameService } from 'src/app/state/gameState/game.service';
import { AudioService } from 'src/app/services/audio.service';
import { UserQuery } from 'src/app/state/userState/user.query';
import _ from 'lodash';
import { BilateralHistoryStatus } from 'src/app/models/game.model';
import moment from 'moment';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { CountriesQuery } from 'src/app/state/countriesState/countries.query';
import { getStanceTranslationKey } from 'src/app/shared/game.utils';

@UntilDestroy()
@Component({
  selector: 'app-book-page-component',
  templateUrl: './book-page.component.html',
  styleUrls: ['./book-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class BookPageComponent implements OnInit {
  topic = signal<ITopic | null>(null);
  dimensionSub: Subscription;
  dimensionIndex: number;
  bookPath = signal('book.svg');
  isHelp = signal(false);
  isInformational = signal(false);
  public helpTopics = signal<IHelpTopic[]>([]);
  public currentCountry: any;
  public page = signal(0);
  public dimensions: IDimension;
  public topicCurrentCountryPosition = signal<ICurrentCountryPosition[] | null>(null);
  selectedIndexes = signal({ topic: 0, subTopic: 0 });
  public history = signal<any[][]>([[], [], []]);
  public alliances = signal<any[][]>([[], [], []]);
  public positions = signal<string[]>(['', '', '']);
  public bilateralHistoryStatus = BilateralHistoryStatus;

  constructor(
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private gameQuery: GameQuery,
    private gameService: GameService,
    private userQuery: UserQuery,
    private audioSerive: AudioService,
    private countryQuery: CountriesQuery
  ) {
    this.dimensionSub = this.gameQuery.selectedDimension$
      .pipe(untilDestroyed(this))
      .subscribe((res) => {
        this.dimensionIndex = res.value - 1;
        const informational = this.gameQuery.getCurrentTopicIsInformational();
        this.isInformational.set(informational);
        if (informational) {
          this.page.set(3);
        } else {
          this.page.set(this.dimensionIndex);
        }
      });
  }

  ngOnInit() {
    // this.page = this.dimensionIndex;
    this.currentCountry = this.gameService.getCurrentUserCountry()?.countryCode;
    combineLatest([this.gameQuery.game$, this.gameQuery.topics$])
      .pipe(untilDestroyed(this))
      .subscribe(([game, topics]) => {
        if (!game || !topics?.length) {
          return;
        }
        const topic = topics.find((t) => t.id === game?.topic);
        this.topic.set(topic);
        this.topicCurrentCountryPosition.set(
          topic?.dimension.map((d) =>
            d.positionContent
              .find((pc) =>
                pc.currentCountryPosition.find(
                  (ccp) => ccp.countryCode === this.currentCountry
                )
              )
              ?.currentCountryPosition.find(
                (ccp) => ccp.countryCode === this.currentCountry
              )
          ) ?? null
        );
      });
    this.gameQuery
      .select('helpTopics')
      .pipe(untilDestroyed(this))
      .subscribe((items) => {
        this.helpTopics.set(items);
      });
    this.getBookPath();
    this.activatedRoute.data.pipe(untilDestroyed(this)).subscribe((data) => {
      this.isHelp.set(data.help);
      if (data.help) {
        this.page.set(3);
      }
    });
  }

  ionViewWillEnter() {
    this.selectedIndexes.set({ topic: 0, subTopic: 0 });
    const userId = this.userQuery.getUserId();
    const game = this.gameQuery.getValue().game;
    const topic = this.gameQuery.getCurrentTopic();
    const newPositions = ['', '', ''];
    const currentUserGame = game?.userGames.find((ug) => ug.userId === userId);
    currentUserGame?.user.userGameNegotiation.forEach((ug) => {
      topic.dimension.some((dimension, index) => {
        if (dimension.id === ug.dimension) {
          dimension.positionContent.some((position) => {
            if (position.id === ug.position) {
              newPositions[index] = position.name;
              return true;
            }
            return false;
          });
          return true;
        }
        return false;
      });
    });
    this.positions.set(newPositions);
    this.gameService.getBilateralHistory();
    this.gameQuery
      .select('bilateralHistory')
      .pipe(untilDestroyed(this))
      .subscribe((bilateralHistory) => {
        const newHistory: any[][] = [[], [], []];
        const newAlliances: any[][] = [[], [], []];
        if (bilateralHistory.length) {
          const userGameNegotiations: { [name: string]: IUserGame } = {};
          game.userGames.forEach((ug) => {
            ug.user.userGameNegotiation.forEach((ugn) => {
              userGameNegotiations[ugn.userGameNegotiationId] = ug;
            });
          });
          const historySorted = _.orderBy(
            bilateralHistory,
            [
              (history) =>
                moment(history.updateDate).format('YYYY-MM-DD HH:mm:ss'),
            ],
            ['desc']
          );
          historySorted
            .filter(
              (history) => history.status !== BilateralHistoryStatus.failed
            )
            .forEach((history) => {
              topic.dimension.some((dimension, index) => {
                if (dimension.id === +history.dimension) {
                  const incoming = history.userId1 !== userId;
                  let positionName = '';
                  dimension.positionContent.some((position) => {
                    if (position.id === +history.position) {
                      positionName = position.name;
                      return true;
                    }
                    return false;
                  });
                  const country = incoming
                    ? userGameNegotiations[history.sendingUserGameNegotiationId]
                        .country
                    : userGameNegotiations[
                        history.receivingUserGameNegotiationId
                      ].country;
                  if (history.status === BilateralHistoryStatus.success) {
                    newAlliances[index].push(country);
                  }
                  if (
                    history.status === BilateralHistoryStatus.success ||
                    history.status.toLowerCase() ===
                      BilateralHistoryStatus.broken
                  ) {
                    newHistory[index].push({
                      country,
                      tradeoff: history.tradeoff,
                      incoming,
                      status: history.status,
                      position: positionName,
                      brokenBy: history.brokenBy,
                    });
                  }
                  return true;
                }
                return false;
              });
            });
        }
        this.history.set(newHistory);
        this.alliances.set(newAlliances);
        this.getBookPath();
      });
  }

  openBook() {
    this.router.navigate(['/book']);
  }

  activePage(index) {
    this.page.set(index);
    this.audioSerive.play('change-page');
    this.getBookPath();
  }

  getStanceClass(stance: string) {
    return stance?.toLowerCase().replace(/ /g, '_');
  }

  getStanceTranslation(stance: string) {
    return getStanceTranslationKey(stance);
  }

  onSubTopicClick = (i1: number, i2: number) => {
    this.selectedIndexes.set({ topic: i1, subTopic: i2 });
  };

  parseCountryCode(countryCode: string) {
    return this.countryQuery.selectCountryByCountryCode(countryCode);
  }

  getBookPath() {
    if (this.history()[this.page()]?.length !== 0) {
      this.bookPath.set('blank_book.svg');
    } else {
      this.bookPath.set('book.svg');
    }
  }

  onBack() {
    this.router.navigate([this.userQuery.getRouterUrl() ?? '/select_mode']);
  }
}
